/**
 * Player demographics — "who are our players", in one screen.
 *
 * admin/manager (gated in `App.tsx`; the API enforces `requireRole('admin','manager')`). Nothing here
 * names an individual — every row is a group — so it sits a step below the owner report in
 * sensitivity and a manager can open it.
 *
 * Charts are plain CSS bars rather than a charting library, on purpose: the dashboard carries a bundle
 * budget (`packages/config/src/bundle-budget.ts`), and a horizontal bar is a `div` with a width. The
 * data shapes are small (a handful of age bands, ~25 municipalities) so nothing here needs
 * virtualisation or a canvas.
 *
 * The coverage line under the age and geography sections is not decoration: date of birth and city are
 * ~88% populated in the source, and a breakdown that quietly dropped the other 12% would overstate its
 * own certainty. The denominator is shown so the reader knows what the bars cover.
 */
import type {
  AgeBandRow,
  ChannelRow,
  DemographicsReport,
  MunicipalityRow,
  RegistrationMonth,
} from '@apuestapr/contracts';
import { Button, icons } from '@apuestapr/ui';
import { useEffect, useMemo, useState, type ReactElement } from 'react';

import type { ApiFailure } from '../api/client.ts';
import { useSessionContext } from '../auth/SessionProvider.tsx';
import { createDemographicsApi } from './demographics-api.ts';

const currency = (amount: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);

const number = (n: number): string => new Intl.NumberFormat('en-US').format(n);
const percentOf = (part: number, whole: number): string =>
  whole > 0 ? `${((part / whole) * 100).toFixed(0)}%` : '—';

/** The default value-overlay window: the last 30 days, ending yesterday (the last settled day). */
const defaultWindow = (): { from: string; to: string } => {
  const to = new Date();
  to.setDate(to.getDate() - 1);
  const from = new Date(to);
  from.setDate(from.getDate() - 29);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
};

const resultTone = (houseResult: number): string =>
  houseResult >= 0 ? 'text-foreground' : 'text-destructive';

/** Spanish label for the fixed age bands; `unknown` is the players without a date of birth. */
const ageBandLabel = (band: string): string => (band === 'unknown' ? 'Sin fecha de nac.' : band);

/** Friendly channel names — the warehouse stores `Mobile`, `Retail_Poc`, `Web`. */
const channelLabel = (channel: string): string => {
  const map: Readonly<Record<string, string>> = {
    Mobile: 'App móvil',
    Retail_Poc: 'Tienda (retail)',
    Web: 'Web',
  };
  return map[channel] ?? channel;
};

const monthLabel = (month: string): string => {
  const [year, m] = month.split('-');
  const names = [
    'ene',
    'feb',
    'mar',
    'abr',
    'may',
    'jun',
    'jul',
    'ago',
    'sep',
    'oct',
    'nov',
    'dic',
  ];
  const index = Number(m) - 1;
  return `${names[index] ?? m} ${year ?? ''}`.trim();
};

/** One labelled horizontal bar: a count, its share of the max, and an optional value caption. */
const Bar = ({
  label,
  count,
  max,
  caption,
}: {
  readonly label: string;
  readonly count: number;
  readonly max: number;
  readonly caption?: string | undefined;
}): ReactElement => {
  const width = max > 0 ? Math.max(2, (count / max) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span className="font-medium">{label}</span>
        <span className="tabular-nums text-muted-foreground">
          {number(count)}
          {caption !== undefined ? <span className="ml-2 text-xs">{caption}</span> : null}
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full bg-primary" style={{ width: `${String(width)}%` }} />
      </div>
    </div>
  );
};

const Panel = ({
  title,
  coverage,
  children,
}: {
  readonly title: string;
  readonly coverage?: string | undefined;
  readonly children: React.ReactNode;
}): ReactElement => (
  <section className="space-y-4 rounded-xl border border-border bg-card p-5 shadow-raised">
    <div className="flex items-baseline justify-between gap-2">
      <h2 className="m-0 text-lg font-semibold">{title}</h2>
      {coverage !== undefined ? (
        <span className="text-xs text-muted-foreground">{coverage}</span>
      ) : null}
    </div>
    {children}
  </section>
);

const AgeSection = ({
  bands,
  coverage,
}: {
  readonly bands: readonly AgeBandRow[];
  readonly coverage: string;
}): ReactElement => {
  const max = Math.max(1, ...bands.map((b) => b.players));
  return (
    <Panel title="Edad de los jugadores" coverage={coverage}>
      <div className="space-y-3">
        {bands.map((band) => (
          <Bar
            key={band.band}
            label={ageBandLabel(band.band)}
            count={band.players}
            max={max}
            caption={
              band.activePlayers > 0
                ? `${number(band.activePlayers)} activos · ${currency(band.handle)} manejo`
                : undefined
            }
          />
        ))}
      </div>
    </Panel>
  );
};

const GeographySection = ({
  municipalities,
  coverage,
}: {
  readonly municipalities: readonly MunicipalityRow[];
  readonly coverage: string;
}): ReactElement => {
  const max = Math.max(1, ...municipalities.map((m) => m.players));
  return (
    <Panel title="Municipios" coverage={coverage}>
      {municipalities.length === 0 ? (
        <p className="text-muted-foreground">Sin datos de municipio.</p>
      ) : (
        <div className="space-y-2.5">
          {municipalities.slice(0, 15).map((muni) => (
            <Bar
              key={muni.municipality}
              label={muni.municipality}
              count={muni.players}
              max={max}
              caption={muni.activePlayers > 0 ? `${number(muni.activePlayers)} activos` : undefined}
            />
          ))}
        </div>
      )}
    </Panel>
  );
};

const ChannelSection = ({
  channels,
}: {
  readonly channels: readonly ChannelRow[];
}): ReactElement => {
  const total = channels.reduce((sum, c) => sum + c.players, 0);
  return (
    <Panel
      title="Cómo juegan (canal)"
      coverage={`${number(total)} jugadores activos en la ventana`}
    >
      {channels.length === 0 ? (
        <p className="text-muted-foreground">Sin actividad en la ventana seleccionada.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Canal</th>
                <th className="px-3 py-2 text-right font-medium">Jugadores</th>
                <th className="px-3 py-2 text-right font-medium">Parte</th>
                <th className="px-3 py-2 text-right font-medium">Manejo</th>
                <th className="px-3 py-2 text-right font-medium">Resultado casa</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {channels.map((channel) => (
                <tr key={channel.channel} className="transition-colors hover:bg-muted/30">
                  <td className="px-3 py-2 font-medium">{channelLabel(channel.channel)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{number(channel.players)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                    {percentOf(channel.players, total)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{currency(channel.handle)}</td>
                  <td
                    className={`px-3 py-2 text-right font-semibold tabular-nums ${resultTone(channel.houseResult)}`}
                  >
                    {currency(channel.houseResult)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
};

const RegistrationsSection = ({
  registrations,
}: {
  readonly registrations: readonly RegistrationMonth[];
}): ReactElement => {
  const max = Math.max(1, ...registrations.map((r) => r.registrations));
  return (
    <Panel title="Registros por mes">
      {registrations.length === 0 ? (
        <p className="text-muted-foreground">Sin datos de registro.</p>
      ) : (
        <div className="flex items-end gap-1.5" style={{ height: '160px' }}>
          {registrations.map((row) => (
            <div key={row.month} className="flex flex-1 flex-col items-center justify-end gap-1">
              <span className="text-[10px] tabular-nums text-muted-foreground">
                {row.registrations}
              </span>
              <div
                className="w-full rounded-t bg-primary"
                style={{ height: `${String(Math.max(2, (row.registrations / max) * 100))}%` }}
                title={`${monthLabel(row.month)}: ${String(row.registrations)}`}
              />
              <span className="origin-left -rotate-45 whitespace-nowrap text-[10px] text-muted-foreground">
                {monthLabel(row.month)}
              </span>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
};

type Loaded =
  | { readonly status: 'ready'; readonly report: DemographicsReport }
  | { readonly status: 'error'; readonly failure: ApiFailure };

type LoadState = { readonly status: 'loading' } | Loaded;

export const DemographicsPage = (): ReactElement => {
  const session = useSessionContext();
  const authenticated = session.state.status === 'authenticated' ? session.state.session : null;
  const api = useMemo(() => createDemographicsApi(session.client), [session.client]);

  const initial = useMemo(() => defaultWindow(), []);
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const run = async (): Promise<void> => {
      setLoading(true);
      const result = await api.get(from, to);
      if (cancelled) return;
      setLoaded(
        result.ok
          ? { status: 'ready', report: result.data }
          : { status: 'error', failure: result.failure },
      );
      setLoading(false);
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [api, from, to, refreshKey]);

  const state: LoadState = loading || loaded === null ? { status: 'loading' } : loaded;

  const reconnect =
    state.status === 'error' && state.failure.kind === 'http' && state.failure.status === 503
      ? state.failure.message
      : null;

  if (!authenticated) return <></>;

  const report = state.status === 'ready' ? state.report : null;
  const ageCoverage =
    report !== null
      ? `${number(report.withDateOfBirth)} de ${number(report.totalPlayers)} con fecha (${percentOf(report.withDateOfBirth, report.totalPlayers)})`
      : '';
  const geoCoverage =
    report !== null
      ? `${number(report.withMunicipality)} de ${number(report.totalPlayers)} con municipio (${percentOf(report.withMunicipality, report.totalPlayers)})`
      : '';

  return (
    <section aria-labelledby="demographics-heading" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 id="demographics-heading" className="font-display text-2xl font-bold">
            Demografía de jugadores
          </h1>
          <p className="text-muted-foreground">
            Perfil de la base de jugadores. Los conteos son de todos los jugadores; el manejo y el
            resultado son de la ventana seleccionada.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <label className="flex flex-col text-xs text-muted-foreground">
            Desde
            <input
              type="date"
              value={from}
              max={to}
              onChange={(event) => setFrom(event.currentTarget.value)}
              className="rounded-lg border border-border bg-card px-2 py-1 text-sm text-foreground"
            />
          </label>
          <label className="flex flex-col text-xs text-muted-foreground">
            Hasta
            <input
              type="date"
              value={to}
              min={from}
              onChange={(event) => setTo(event.currentTarget.value)}
              className="rounded-lg border border-border bg-card px-2 py-1 text-sm text-foreground"
            />
          </label>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setRefreshKey((k) => k + 1)}
            disabled={state.status === 'loading'}
          >
            <icons.refresh className="h-4 w-4" />
            {state.status === 'loading' ? 'Cargando…' : 'Actualizar'}
          </Button>
        </div>
      </div>

      {reconnect !== null ? (
        <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-4" role="alert">
          <p className="m-0 font-medium text-destructive">No se pudo conectar con Snowflake</p>
          <p className="m-0 mt-1 text-sm text-muted-foreground">{reconnect}</p>
        </div>
      ) : null}

      {state.status === 'error' && reconnect === null ? (
        <p role="alert">No se pudo cargar la demografía. Inténtalo de nuevo.</p>
      ) : null}

      {state.status === 'loading' ? (
        <div className="flex items-center justify-center py-10">
          <div className="h-6 w-6 animate-spin rounded-full border-4 border-muted border-t-primary" />
        </div>
      ) : null}

      {report !== null ? (
        <div className="grid gap-6 lg:grid-cols-2">
          <AgeSection bands={report.ageBands} coverage={ageCoverage} />
          <GeographySection municipalities={report.municipalities} coverage={geoCoverage} />
          <ChannelSection channels={report.channels} />
          <RegistrationsSection registrations={report.registrations} />
        </div>
      ) : null}
    </section>
  );
};
